<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import ajax, { prepareUrl } from '../../ajax/ajax';
    import ScreenplayEditor from "../../ScreenplayEditor/ScreenplayEditor.svelte";
    import getProjectStore, { flattenTree } from "../../stores/ProjectStore";
    import IconButton from "../buttons/IconButton.svelte";
    import SavedIndicator from "../indicator/SavedIndicator.svelte";
    import CloseIcon from "../icons/CloseIcon.svelte";
    import type Tab from "./Tab";
    import NotesEditor from "../../text/NotesEditor.svelte";
    import ImageEditor from "../../image/ImageEditor.svelte";
    import Snackbar from "../snackbar/Snackbar.svelte";
    import { encode } from 'js-base64';
    import getToken from '../../auth/token';

    export let projectId: number;

    let lastActiveTab: string|null = null; 

    let tabs: Tab[] = [];
    let editorContent: HTMLElement;
    let editorWindowWidth: number;
    let tabBar: HTMLElement;
    let isScreenplay = true;
    let timeout: number | null = null;
    let errorMessage = null;
    const renderedComponents: {
        [key: string]: ScreenplayEditor | NotesEditor | ImageEditor;
    } = {};
    const projectStore = getProjectStore(projectId);

    function setTabBarSize(node: HTMLElement | undefined) {
        if (node && editorWindowWidth) {
            let padding = isScreenplay ? 110 : 0;
            node.style.maxWidth = `${editorWindowWidth - padding}px`;
        }
    }

    function getErrorMessageFromAjax(
        xhr: XMLHttpRequest,
        fallback: string
    ): string {
        try {
            const returnValue = JSON.parse(xhr.responseText);
            if (returnValue) {
                return returnValue.errorMessage
                    ? returnValue.errorMessage
                    : fallback;
            }
        } catch {}

        return fallback;
    }

    onMount(() => {
        function onResize() {
            setTabBarSize(tabBar);
        }
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        };
    });

    function initTabBar(node) {
        setTabBarSize(node);
    }

    async function getSreenplay(path: string): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            ajax.post(
                `/projects/${projectId}/tree/text`,
                { path },
                (data: { text: string }) => {
                    resolve(data.text);
                },
                (s, n, xhr) => {
                    reject(xhr);
                }
            );
        });
    }

    function getImageUrl(path: string): string {
      return encode(path);
    }

    function openScreenplay(tab: Tab, div: HTMLElement) {
        getSreenplay(tab.getPath())
            .then((screenplay: string) => {
                div.innerHTML = "";
                renderedComponents[tab.getId()] = new ScreenplayEditor({
                    target: div,
                    props: {
                        projectId: projectId,
                        screenplay: screenplay,
                        identifier: tab.getPath(),
                        setSavedState: (saved: boolean) => {
                            projectStore.setSavedState(tab.getId(), saved);
                        },
                        allowSave: () => {
                            return !!tabs.find((candidate) => {
                                return (
                                    candidate.isActive() &&
                                    candidate.getId() === tab.getId()
                                );
                            });
                        },
                        saveScreenplay: (screenplayToSave: string) => {
                            return new Promise((resolve, reject) => {
                                ajax.put(
                                    `/projects/${projectId}/tree/text`,
                                    {
                                        path: tab.getPath(),
                                        text: screenplayToSave,
                                    },
                                    () => {
                                        resolve();
                                    },
                                    (s, n, xhr) => {
                                        if (null !== timeout) {
                                            clearTimeout(timeout);
                                        }
                                        errorMessage = getErrorMessageFromAjax(
                                            xhr,
                                            "Could not get text from server."
                                        );
                                        timeout = setTimeout(() => {
                                            errorMessage = null;
                                        }, 10000);
                                        reject();
                                    }
                                );
                            });
                        },
                    },
                });
            })
            .catch((xhr) => {
                if (null !== timeout) {
                    clearTimeout(timeout);
                }
                errorMessage = getErrorMessageFromAjax(
                    xhr,
                    "Could not get text from server."
                );
                timeout = setTimeout(() => {
                    errorMessage = null;
                }, 10000);
            });
    }

    function openNote(tab: Tab, div: HTMLElement) {
        getSreenplay(tab.getPath()).then((text: string) => {
            div.innerHTML = "";
            renderedComponents[tab.getId()] = new NotesEditor({
                target: div,
                props: {
                    text: text,
                    setSavedState: (saved: boolean) => {
                        projectStore.setSavedState(tab.getId(), saved);
                    },
                    allowSave: () => {
                        return !!tabs.find((candidate) => {
                            return (
                                candidate.isActive() &&
                                candidate.getId() === tab.getId()
                            );
                        });
                    },
                    saveText: (textToSave: string) => {
                        return new Promise((resolve, reject) => {
                            ajax.put(
                                `/projects/${projectId}/tree/text`,
                                { path: tab.getPath(), text: textToSave },
                                () => {
                                    resolve();
                                },
                                (s, n, xhr) => {
                                    if (null !== timeout) {
                                        clearTimeout(timeout);
                                    }
                                    errorMessage = getErrorMessageFromAjax(
                                        xhr,
                                        "Could not get text from server."
                                    );
                                    timeout = setTimeout(() => {
                                        errorMessage = null;
                                    }, 10000);
                                    reject();
                                }
                            );
                        });
                    },
                },
            });
        });
    }

    function openImage(tab: Tab, div: HTMLElement) {
        const url = getImageUrl(tab.getPath());
        const realUrl = prepareUrl(`/projects/${projectId}/files/${encodeURIComponent(url)}?token=${encodeURIComponent(getToken())}`);
        div.innerHTML = "";
        renderedComponents[tab.getId()] = new ImageEditor({
          target: div,
          props: {
            url: realUrl,
            path: tab.getPath(),
          },
        });
    }

    function updateTabContent() {
        if (editorContent) {
            const validTabIds = [];
            tabs.forEach((tab) => {
                if (tab.isActive()) {
                    isScreenplay = tab.getExtension() === ".fountain";
                }
                validTabIds.push(tab.getId());
                if (!renderedComponents[tab.getId()]) {
                    const div = document.createElement("div");
                    div.setAttribute("data-id", "t" + tab.getId());
                    div.innerHTML = "Loading...";
                    div.style.display = tab.isActive() ? "block" : "none";
                    editorContent.appendChild(div);
                    if (tab.getExtension() === ".fountain") {
                        openScreenplay(tab, div);
                    } else if (tab.getExtension() === ".notes") {
                        openNote(tab, div);
                    }
                    else if (['.jpg', '.jpeg', '.png', '.gif'].indexOf(tab.getExtension().toLowerCase()) !== -1) {
                      openImage(tab, div);
                    }
                } else {
                    if (tab.isActive()) {
                        if  (lastActiveTab !== tab.getPath() && lastActiveTab !== null) {
                            document.dispatchEvent(new Event('tabEnable'+tab.getPath()))
                        }
                        lastActiveTab = tab.getPath();
                    }
                    const div = editorContent.querySelector<HTMLElement>(
                        `div[data-id="t${tab.getId()}"]`
                    );
                    if (div) {
                        div.style.display = tab.isActive() ? "block" : "none";
                    }
                }
            });
            Object.keys(renderedComponents).forEach((contentTabId) => {
                if (validTabIds.indexOf(contentTabId) === -1) {
                    renderedComponents[contentTabId].$destroy();
                    delete renderedComponents[contentTabId];
                    const div = editorContent.querySelector<HTMLElement>(
                        `div[data-id="t${contentTabId}"]`
                    );
                    if (div) {
                        div.parentNode.removeChild(div);
                    }
                }
            });
            setTabBarSize(tabBar);
        }
    }

    const unsubscribe = projectStore.subscribe((projectValue) => {
        tabs = projectValue.state.tabs;
        updateTabContent();
    });

    onDestroy(() => {
        unsubscribe();
        Object.values(renderedComponents).forEach((component) => {
            component.$destroy();
        });
    });

    let activeContextmenu: string | null = null;
</script>

<div class="EditorWindow" bind:clientWidth={editorWindowWidth}>
    {#if tabs.length > 0}
        <div class="TabBar" bind:this={tabBar} use:initTabBar>
            {#each tabs as tab (tab.getId())}
                <span
                    class="Tab"
                    on:click|stopPropagation={() => {
                        projectStore.setShownInEditor(tab.getId(), true);
                    }}
                    on:contextmenu|stopPropagation|preventDefault={() => {
                        if (activeContextmenu === tab.getId()) {
                            activeContextmenu = null;
                        } else {
                            activeContextmenu = tab.getId();
                        }
                    }}
                    class:active={tab.isActive()}
                    >{tab.getName()}
                    <SavedIndicator saved={tab.isSaved()} />
                    <IconButton
                        title="Close"
                        icon={CloseIcon}
                        width="16px"
                        height="16px"
                        onClick={() => {
                            if (tab.isSaved()) {
                                projectStore.setShownInEditor(
                                    tab.getId(),
                                    false
                                );
                            } else {
                                if (confirm(`Close without saving?`)) {
                                    projectStore.setShownInEditor(
                                        tab.getId(),
                                        false
                                    );
                                }
                            }
                        }}
                    />
                    {#if activeContextmenu === tab.getId()}
                        <div class="ContextMenu">
                            <button
                                type="button"
                                on:click={() => {
                                    const unsaved = [];
                                    tabs.forEach((otherTab) => {
                                        if (otherTab.getId() === tab.getId()) {
                                            return;
                                        }
                                        if (!otherTab.isSaved()) {
                                            unsaved.push(otherTab.getName());
                                        }
                                    });
                                    if (unsaved.length === 0) {
                                        projectStore.closeTabsExcept(tab.getId());
                                    } else {
                                        if (confirm(`Close unsaved tabs? [${unsaved.join(', ')}]`)) {
                                            projectStore.closeTabsExcept(tab.getId());
                                        }
                                    }
                                    activeContextmenu = null;
                                }}
                            >
                                Close others
                            </button>
                        </div>
                    {/if}
                </span>
            {/each}
        </div>
    {:else}
        <div class="NoFilesOpen">No open files.</div>
    {/if}
    <div class="EditorContent" bind:this={editorContent} />
</div>
{#if errorMessage}
    <Snackbar type="error" message={errorMessage} />
{/if}

<style type="text/scss">
    @import "../../scss/config.scss";
    .EditorWindow {
        display: flex;
        flex-direction: column;
        height: 100vh;
    }
    .TabBar {
        min-height: 40px;
        max-height: 40px;
        max-width: 500px;
        background: $color-light-grey;
        display: flex;
        overflow: hidden;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: -ms-autohiding-scrollbar;
        &::-webkit-scrollbar {
            height: 2px;
        }
        &::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.1);
        }
        &::-webkit-scrollbar-thumb {
            width: 2px;
            background: rgba(0, 0, 0, 0.2);
            border-radius: 4px;
        }
        &::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.5);
        }
    }
    .EditorContent {
        flex-grow: 1;
        height: 100vh;
        overflow: auto;
        overflow-y: scroll;
        &::-webkit-scrollbar {
            width: 8px;
        }

        &::-webkit-scrollbar-track {
            //background: rgba(0, 0, 0, 0.1);
            background: #fff;
        }

        &::-webkit-scrollbar-thumb {
            width: 8px;
            background: rgba(0, 0, 0, 0.2);
            border-radius: 0;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.5);
        }
    }
    .Tab {
        display: inline-block;
        line-height: 30px;
        padding: 5px 10px;
        background: darken($color: $color-light-grey, $amount: 2);
        cursor: pointer;
        white-space: nowrap;
        position: relative;
        &.active {
            background: $color-white;
        }
        .ContextMenu {
            position: absolute;
            left: 100%;
            top: 1px;
            z-index: 2;
            div {
                background: red;
            }
        }
    }
    .NoFilesOpen {
        text-align: center;
        padding-top: 100px;
    }
</style>
